import { PaxCheckDocumentProvisionMethod } from "api/models";
import Content, { ContentNS } from "content/content";
import OutlinedButton from "features/shared/components/buttons/OutlinedButton.styled";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import UploadButton from "features/shared/components/buttons/upload-button/UploadButton";
import useDemoScenario from "features/shared/hooks/demo/useDemoScenario";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import useDocumentImagesUpload from "features/shared/hooks/useDocumentImagesUpload";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import { DocProcessingStatus } from "models/doc-processing-status";
import AllowedDocument from "models/documents/allowed-document";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AnalyticsService from "services/analytics/analytics";
import { getScannerDocumentConfig } from "services/document-capture/scanner";
import { imagesToBase64 } from "services/document-capture/utils";
import growthbook from "services/growthbook/growthbook";
import { useAppStore } from "store/store";
import { v4 as uuidv4 } from "uuid";

import useScannerAnalytics from "../shared/hooks/analytics/useScannerAnalytics";
import useItemLevelRedirect from "../shared/hooks/navigation/useItemLevelRedirect";
import Capturer from "./Capturer";
import ScanningResultsPreview from "./components/results-preview/ScanningResultsPreview";
import ScannerOverlay from "./components/scanner-overlay/ScannerOverlay";
import ScanningWarningBanner from "./components/scanning-warning-banner/ScanningWarningBanner";
import useAllowedDocumentsForTravelDoc from "./hooks/useAllowedDocumentsForTravelDoc";
import useDeviceCamera from "./hooks/useDeviceCamera";
import useScannerSelection from "./hooks/useScannerSelection";
import useDrawScannedImagesOnCanvas from "./hooks/useDrawScannedImagesOnCanvas";
import getScanningStages from "./logic/getScanningStages";
import handleCapturedDocumentScan from "./scan-handlers/handleCapturedDocumentScan";
import ButtonGroup from "./styles/ButtonGroup.styled";
import ScanningCard from "./styles/ScanningCard.styled";

export enum ScannerStatus {
  Scanning = "Scanning",
  ScanningComplete = "ScanningComplete",
}

export enum ScannerOverlayState {
  ScanningNotSupported = "ScanningNotSupported",

  ScanningDataPage = "ScanningDataPage",

  BeforeScanningFrontSide = "BeforeScanningFrontSide",
  ScanningFrontSide = "ScanningFrontSide",

  BeforeScanningBackSide = "BeforeScanningBackSide",
  ScanningBackSide = "ScanningBackSide",
}

export enum LowResolutionOverlayStatus {
  Hidden,
  InitialWarning,
  AfterReScanRequest,
}

interface CaptureContentProps {
  itemType: string;
  scanId: string;
  docs: AllowedDocument[];
}

interface CaptureByItemProps {
  item: DocumentRequirement;
  scanId: string;
  forceDocumentType: string | undefined;
}

interface CaptureByTypeProps {
  itemType: string;
  scanId: string;
  forceDocumentType: string | undefined;
}

const CaptureContent: FC<CaptureContentProps> = ({
  itemType,
  scanId,
  docs,
}) => {
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const navigate = useNavigateWithQuery();

  const sendScannerAnalyticsEvent = useScannerAnalytics(itemType);

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const navigateToItemPage = useItemLevelRedirect(itemLevelNavigationState);

  const uploadFiles = useDocumentImagesUpload(
    PaxCheckDocumentProvisionMethod.SCAN
  );

  const { sessionId } = useParams();

  const paxSessionId = usePaxSession()?.paxSessionId;

  const docScannerConfig = getScannerDocumentConfig(docs);

  const [scannerStatus, setScannerStatus] = useState(ScannerStatus.Scanning);
  const scannerError = useAppStore((state) => state.scannerError);

  const stages = getScanningStages(docScannerConfig);

  const setSelectedScannerType = useAppStore(
    (state) => state.setSelectedScannerType
  );

  const [scannerState, setScannerState] = useState<ScannerOverlayState>(
    stages.length > 0 ? stages[0] : ScannerOverlayState.ScanningNotSupported
  );

  // Resolve which scanner this scan uses (A/B flag), latched for the scan's
  // lifetime (useState keeps the first render's value) so a late Growthbook
  // feature load can't swap the scanner — and restart the camera — mid-flow.
  const liveSelectedScanner = useScannerSelection();
  const [selectedScanner] = useState(liveSelectedScanner);

  const [stream, isLowResolution] = useDeviceCamera(selectedScanner);

  const [lowResOverlayStatus, setLowResOverlayStatus] = useState(
    LowResolutionOverlayStatus.InitialWarning
  );

  const [resultImages, setResultImages] = useState<ImageData[]>([]);

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const demoScenario = useDemoScenario(itemType);

  const moveScannerToNextState = () => {
    setScannerState((prevScannerState) => {
      const nextIndex = Math.min(
        stages.indexOf(prevScannerState) + 1,
        stages.length - 1
      );

      return stages[nextIndex];
    });
  };

  const startScan = useCallback(() => {
    moveScannerToNextState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const onCancelCallbackRef = React.useRef<() => void>(null);
  const registerOnCancelCallback = (callback: () => void) => {
    onCancelCallbackRef.current = callback;
  };

  const onReScanCallbackRef = React.useRef<() => void>(null);
  const registerOnReScanCallback = (callback: () => void) => {
    onReScanCallbackRef.current = callback;
  };

  const onUploadProcessStartedCallbackRef = React.useRef<() => void>(null);
  const registerOnUploadProcessStartedCallback = (callback: () => void) => {
    onUploadProcessStartedCallbackRef.current = callback;
  };

  const onCancel = () => {
    // call registered callback from child
    onCancelCallbackRef.current?.();
    sendScannerAnalyticsEvent("scanner_closed");
    setScannerStatus(ScannerStatus.Scanning);
    navigateToItemPage();
  };

  const onReScan = () => {
    // call registered callback from child
    onReScanCallbackRef.current?.();

    sendScannerAnalyticsEvent("re_scan_chosen");
    setScannerState(
      stages.length > 0 ? stages[0] : ScannerOverlayState.ScanningNotSupported
    );
    setScannerStatus(ScannerStatus.Scanning);
  };

  const onUploadProcessStarted = () => {
    // call registered callback from child
    onUploadProcessStartedCallbackRef.current?.();
  };

  const onConfirm = () => {
    navigate("../processing");

    setProcessingStatus(DocProcessingStatus.InProgress, null);
    setProcessingMayTakeLong(
      docs.some((doc) => doc.expectLongProcessing === true)
    );

    if (demoScenario !== undefined) {
      // demo mode
      handleCapturedDocumentScan(
        [demoScenario.fileContentBase64],
        uploadFiles,
        demoScenario.fileName
      );
    } else {
      handleCapturedDocumentScan(
        imagesToBase64(resultImages),
        uploadFiles,
        undefined,
        undefined,
        docs.length === 1 ? docs[0].pageIndexToProcess ?? undefined : undefined
      );
    }
  };

  const uploadButton = (
    <UploadButton
      primary
      docs={docs}
      onUploadProcessStarted={onUploadProcessStarted}
    />
  );

  useDrawScannedImagesOnCanvas(resultImages);

  useEffect(() => {
    growthbook.setAttributes({
      sessionId: sessionId,
      paxSessionId: paxSessionId,
      scanId: scanId,
    });

    // set scanId in analytics service - to be sent with scan events
    AnalyticsService.shared.setScanId(scanId);
  }, [sessionId, paxSessionId, scanId]);

  useEffect(() => {
    // send once at render
    sendScannerAnalyticsEvent("scanner_selected");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // send analytics events
  useEffect(() => {
    if (scannerError) {
      sendScannerAnalyticsEvent("scanner_fail");
      sendScannerAnalyticsEvent("scanner_error_shown");
      return;
    }

    if (isLowResolution) {
      sendScannerAnalyticsEvent("low_res_warning_shown");
      return;
    }

    if (scannerStatus === ScannerStatus.ScanningComplete) {
      sendScannerAnalyticsEvent("scanner_success");
      return;
    }
  }, [scannerStatus, isLowResolution, scannerError, sendScannerAnalyticsEvent]);

  // demo mode
  useEffect(() => {
    function handleKeyDown(e: { keyCode: number }) {
      if (demoScenario !== undefined && e.keyCode === 90) {
        // call registered callback from child
        onCancelCallbackRef.current?.();
        setScannerStatus(ScannerStatus.ScanningComplete);
        onConfirm();
      }
    }

    if (demoScenario !== undefined) {
      document.addEventListener("keydown", handleKeyDown);

      // Don't forget to clean up
      return function cleanup() {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }

    return;
    // eslint-disable-next-line
  }, [demoScenario]);

  // on scan success - show captured image(s) back to user to confirm
  if (scannerStatus === ScannerStatus.ScanningComplete) {
    return (
      <ScanningCard>
        <Summary>{textUI("scanning")}</Summary>
        <ScanningResultsPreview images={resultImages} />
        <ButtonGroup className="with-rescan-button">
          <OutlinedButton onClick={onCancel}>
            {textUI("button.cancel")}
          </OutlinedButton>
          <OutlinedButton onClick={onReScan}>
            {textUI("button.re-scan")}
          </OutlinedButton>
          <SubmitButton onClick={onConfirm}>
            {textUI("button.confirm")}
          </SubmitButton>
        </ButtonGroup>
      </ScanningCard>
    );
  }

  // on scanner error OR low res + scanning is disallowed for low res via config
  if (
    scannerError ||
    (isLowResolution &&
      process.env.REACT_APP_SCANNER_DISALLOW_ON_LOW_RES !== undefined &&
      process.env.REACT_APP_SCANNER_DISALLOW_ON_LOW_RES === "true")
  ) {
    return (
      <ScanningCard>
        <Summary>{textUI("scanning")}</Summary>
        <ScanningWarningBanner text={textUI("scanner-error")} isError={true} />
        <ButtonGroup>
          <OutlinedButton onClick={onCancel}>
            {textUI("button.cancel")}
          </OutlinedButton>
          {uploadButton}
        </ButtonGroup>
      </ScanningCard>
    );
  }

  const overlay =
    !isLowResolution ||
    lowResOverlayStatus === LowResolutionOverlayStatus.Hidden ||
    lowResOverlayStatus === LowResolutionOverlayStatus.AfterReScanRequest ? (
      <ScannerOverlay
        scannerState={scannerState}
        docScannerConfig={docScannerConfig}
        startScanning={startScan}
      />
    ) : null;

  return (
    <ScanningCard>
      <Summary>{textUI("scanning")}</Summary>
      <Capturer
        selectedScanner={selectedScanner}
        paxSessionId={paxSessionId}
        itemType={itemType}
        docScannerConfig={docScannerConfig}
        overlay={overlay}
        uploadButton={uploadButton}
        // hooks
        setResultImages={setResultImages}
        scannerStatus={scannerStatus}
        setSelectedScannerType={setSelectedScannerType}
        setScannerStatus={setScannerStatus}
        moveScannerToNextState={moveScannerToNextState}
        lowResOverlayStatus={lowResOverlayStatus}
        setLowResOverlayStatus={setLowResOverlayStatus}
        sendScannerAnalyticsEvent={sendScannerAnalyticsEvent}
        // events from parent to trigger
        onCancel={onCancel}
        onUploadProcessStarted={onUploadProcessStarted}
        cameraStream={stream}
        isLowResolution={isLowResolution}
        // other callbacks
        registerOnCancelCallback={registerOnCancelCallback}
        registerOnReScanCallback={registerOnReScanCallback}
        registerOnUploadProcessStartedCallback={
          registerOnUploadProcessStartedCallback
        }
      ></Capturer>
    </ScanningCard>
  );
};

const CaptureByItem: FC<CaptureByItemProps> = ({
  item,
  scanId,
  forceDocumentType,
}) => {
  // take allowed document by forceDocumentType (if provided) from item
  const forcedDocument = forceDocumentType
    ? item.getAllowedDocumentByType(forceDocumentType)
    : undefined;

  // take forcedDocument if present,
  // otherwise take allowed documents from item
  const docs = forcedDocument
    ? [forcedDocument]
    : item.allowedDocumentsSet.allowedDocuments;

  return (
    <CaptureContent itemType={item.itemType} docs={docs} scanId={scanId} />
  );
};

const CaptureByType: FC<CaptureByTypeProps> = ({
  itemType,
  scanId,
  forceDocumentType,
}) => {
  const defaultAllowedDocsForTravelDocument = useAllowedDocumentsForTravelDoc();

  // take allowed document by forceDocumentType (if provided)
  const forcedDocument = forceDocumentType
    ? defaultAllowedDocsForTravelDocument.find(
        (doc) => doc.type === forceDocumentType
      )
    : undefined;

  // take forcedDocument if present,
  // otherwise take all allowed travel docs from session
  const docs = forcedDocument
    ? [forcedDocument]
    : defaultAllowedDocsForTravelDocument;

  return <CaptureContent itemType={itemType} docs={docs} scanId={scanId} />;
};

const DocumentCapture: FC = () => {
  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  // generate random scanId - for growthbook a/b test (MB scanner)
  const scanAttemptId = uuidv4();

  // itemId
  if (itemLevelNavigationState instanceof ItemNavigationState) {
    const item = itemLevelNavigationState.item;
    if (item instanceof DocumentRequirement === false) {
      throw new ItemLevelNavigationError(
        sessionId,
        paxSession?.paxSessionId,
        `Document capture is available only for DocumentRequirement,` +
          `item type provided: ${item.itemType}, item id: ${item.id}`
      );
    }

    return (
      <CaptureByItem
        item={itemLevelNavigationState.item as DocumentRequirement}
        forceDocumentType={itemLevelNavigationState.forceDocumentType}
        scanId={scanAttemptId}
      />
    );
  }

  // itemType
  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    return (
      <CaptureByType
        itemType={itemLevelNavigationState.itemType}
        forceDocumentType={itemLevelNavigationState.forceDocumentType}
        scanId={scanAttemptId}
      />
    );
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default DocumentCapture;
